import { supabase } from './supabase';
import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Classe para gerenciar autenticação segura com Supabase
 * Garante que apenas tokens válidos sejam utilizados nas consultas
 */
class SecureSupabaseClient {
  private client: SupabaseClient;
  private lastTokenValidation: number = 0;
  private validationInterval: number = 5 * 60 * 1000; // 5 minutos

  constructor(client: SupabaseClient) {
    this.client = client;
  }

  /**
   * Valida se o token atual é válido e não expirado
   */
  private async validateToken(): Promise<boolean> {
    try {
      const now = Date.now();
      
      // Se validamos recentemente, não validar novamente
      if (now - this.lastTokenValidation < this.validationInterval) {
        return true;
      }

      const { data: { session }, error } = await this.client.auth.getSession();
      
      if (error || !session) {
        console.warn('Sessão inválida ou não encontrada:', error?.message);
        return false;
      }

      // Verificar se o token não está expirado
      const expiresAt = (session.expires_at || 0) * 1000; // Converter para milliseconds
      const timeUntilExpiry = expiresAt - now;

      if (timeUntilExpiry <= 0) {
        console.warn('Token expirado, tentando renovar...');
        
        // Tentar renovar o token
        const { data: refreshData, error: refreshError } = await this.client.auth.refreshSession();
        
        if (refreshError || !refreshData.session) {
          console.error('Falha ao renovar token:', refreshError?.message);
          return false;
        }
        
        console.log('Token renovado com sucesso');
      }

      // Fazer um teste simples para verificar se o token funciona com RLS
      const { error: testError } = await this.client
        .from('accounts')
        .select('id')
        .limit(1);
      
      if (testError) {
        console.error('Token não funciona com RLS:', testError.message);
        return false;
      }

      this.lastTokenValidation = now;
      return true;
      
    } catch (error) {
      console.error('Erro na validação do token:', error);
      return false;
    }
  }

  /**
   * Executa uma consulta apenas se o token for válido
   */
  async secureQuery<T>(queryFn: () => Promise<T>): Promise<T> {
    const isValid = await this.validateToken();
    
    if (!isValid) {
      throw new Error('Token de autenticação inválido ou expirado. Faça login novamente.');
    }

    try {
      return await queryFn();
    } catch (error: any) {
      // Se o erro for relacionado à autenticação, invalidar cache de validação
      if (error?.code === 'PGRST301' || error?.message?.includes('JWT')) {
        this.lastTokenValidation = 0;
        throw new Error('Erro de autenticação. Faça login novamente.');
      }
      
      throw error;
    }
  }

  /**
   * Getter para acessar o cliente Supabase original (para casos especiais)
   */
  get rawClient(): SupabaseClient {
    return this.client;
  }
}

// Instância global do cliente seguro
export const secureSupabase = new SecureSupabaseClient(supabase);

/**
 * Hook para usar o cliente Supabase com validação de token
 */
export function useSecureSupabase() {
  return {
    /**
     * Executa uma consulta com validação de token
     */
    query: async <T>(queryFn: () => Promise<T>): Promise<T> => {
      return secureSupabase.secureQuery(queryFn);
    },
    
    /**
     * Acesso direto ao cliente (use com cuidado)
     */
    client: secureSupabase.rawClient
  };
}

/**
 * Função utilitária para executar consultas seguras
 */
export async function executeSecureQuery<T>(queryFn: () => Promise<T>): Promise<T> {
  return secureSupabase.secureQuery(queryFn);
}

/**
 * Função para verificar se o usuário está autenticado com token válido
 */
export async function isUserAuthenticated(): Promise<boolean> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error || !session || !session.user) {
      return false;
    }

    // Verificar se o token não está expirado
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session.expires_at || 0;
    
    if (expiresAt <= now) {
      return false;
    }

    // Para usuários autenticados, fazer um teste simples e não crítico
    // Se falhar, ainda consideramos autenticado mas logamos o erro
    try {
      const { error: testError } = await supabase
        .from('accounts')
        .select('id')
        .limit(1);
      
      if (testError) {
        console.warn('Aviso: Possível problema com RLS ou permissões:', testError.message);
        // Ainda retorna true pois o usuário tem sessão válida
      }
    } catch (testError) {
      console.warn('Aviso: Não foi possível testar RLS:', testError);
      // Ainda retorna true pois o usuário tem sessão válida
    }
    
    return true;
    
  } catch (error) {
    console.error('Erro ao verificar autenticação:', error);
    return false;
  }
}

/**
 * Função para lidar com tokens inválidos
 * IMPORTANTE: Não redireciona automaticamente - deixa para o AuthProvider decidir
 */
export async function handleInvalidToken(): Promise<void> {
  try {
    await supabase.auth.signOut();
    
    // REMOVIDO: Redirecionamento automático
    // O redirecionamento deve ser controlado pelo AuthProvider
    // para respeitar a lógica de roteamento (ex: landing page na rota raiz)
    console.log('Token inválido - usuário deslogado. Redirecionamento controlado pelo AuthProvider.');
  } catch (error) {
    console.error('Erro ao fazer logout:', error);
    
    // REMOVIDO: Redirecionamento forçado
    // Mesmo com erro, o AuthProvider deve controlar o redirecionamento
    console.log('Erro no logout - AuthProvider controlará o redirecionamento.');
  }
}

/**
 * Middleware para interceptar erros de autenticação
 */
export function setupAuthErrorInterceptor(): void {
  // Interceptar erros globais relacionados à autenticação
  const originalConsoleError = console.error;
  
  console.error = (...args: any[]) => {
    const message = args.join(' ');
    
    // Verificar se é um erro de autenticação
    if (message.includes('JWT') || 
        message.includes('PGRST301') || 
        message.includes('invalid_token') ||
        message.includes('token_expired')) {
      
      console.warn('Erro de autenticação detectado, redirecionando para login...');
      handleInvalidToken();
    }
    
    // Chamar console.error original
    originalConsoleError.apply(console, args);
  };
}

/**
 * Função para inicializar o sistema de autenticação segura
 */
export function initializeSecureAuth(): void {
  setupAuthErrorInterceptor();
  
  // REMOVIDO: Verificação automática que causava redirecionamento indevido
  // A verificação de autenticação deve ser feita apenas pelo AuthProvider
  // para evitar conflitos com a lógica de roteamento
}
